export type SendOptions = {
  /** The page is being hidden or closed, the request has to start synchronously and survive unload. */
  unloading: boolean;
};

export type EventQueueOptions<T> = {
  flushIntervalMs: number;
  maxBatchSize: number;
  /** Fetch `keepalive` requests share a 64KB budget per page, batches have to stay well below it. */
  maxBatchBytes: number;
  maxAttempts: number;
  send: (batch: T[], options: SendOptions) => Promise<void>;
  isRetryable: (error: unknown) => boolean;
  onDrop: (batch: T[], error: unknown) => void;
};

type Entry<T> = { item: T; bytes: number; attempts: number };

const encoder = new TextEncoder();

export const createEventQueue = <T>(options: EventQueueOptions<T>) => {
  let queue: Entry<T>[] = [];
  let timer: ReturnType<typeof setTimeout> | undefined;
  const inFlight = new Set<Promise<void>>();

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') void flush({ unloading: true });
  };
  const onPageHide = () => void flush({ unloading: true });
  let listening = false;

  const listen = () => {
    if (listening || typeof window === 'undefined') return;
    listening = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  };

  const schedule = () => {
    if (timer !== undefined || queue.length === 0) return;
    timer = setTimeout(() => {
      timer = undefined;
      void flush();
    }, options.flushIntervalMs);
  };

  const takeBatch = () => {
    let bytes = 0;
    let count = 0;
    for (const entry of queue) {
      if (count >= options.maxBatchSize) break;
      if (count > 0 && bytes + entry.bytes > options.maxBatchBytes) break;
      bytes += entry.bytes;
      count++;
    }
    return queue.splice(0, count);
  };

  const sendBatch = async (batch: Entry<T>[], sendOptions: SendOptions) => {
    try {
      await options.send(
        batch.map((entry) => entry.item),
        sendOptions,
      );
    } catch (error) {
      const retryable = options.isRetryable(error);
      const retry: Entry<T>[] = [];
      const dropped: T[] = [];

      for (const entry of batch) {
        if (retryable && entry.attempts + 1 < options.maxAttempts) {
          retry.push({ ...entry, attempts: entry.attempts + 1 });
        } else {
          dropped.push(entry.item);
        }
      }

      if (dropped.length > 0) options.onDrop(dropped, error);
      if (retry.length > 0) {
        queue = [...retry, ...queue];
        schedule();
      }
    }
  };

  const flush = (sendOptions: SendOptions = { unloading: false }) => {
    clearTimeout(timer);
    timer = undefined;

    while (queue.length > 0) {
      const promise = sendBatch(takeBatch(), sendOptions).finally(() => inFlight.delete(promise));
      inFlight.add(promise);
    }

    return Promise.all(inFlight).then(() => undefined);
  };

  const enqueue = (item: T) => {
    listen();
    queue.push({ item, bytes: encoder.encode(JSON.stringify(item)).length, attempts: 0 });

    if (queue.length >= options.maxBatchSize) {
      void flush();
    } else {
      schedule();
    }
  };

  /** Flushes what's left and detaches page lifecycle listeners. */
  const dispose = () => {
    if (listening) {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      listening = false;
    }
    return flush();
  };

  return { enqueue, flush, dispose, size: () => queue.length };
};

export type EventQueue<T> = ReturnType<typeof createEventQueue<T>>;
